import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Users, Plus, Trash2, Gift, Copy, Sparkles, ArrowLeft, ShieldAlert, CalendarDays, MessageCircle, Mail, Check,
} from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../i18n/LanguageContext";
import { api, formatApiError } from "../lib/api";
import { Loader } from "../components/shared";
import BackToHome from "../components/BackToHome";
import { maskPhone, toStoredPhone, displayPhone, PHONE_EXAMPLE } from "../lib/phone";

function daysUntil(dateStr) {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr + "T00:00:00") - new Date()) / 86400000);
}

export default function SecretSantaGroup() {
  const { t, lang } = useI18n();
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [pName, setPName] = useState("");
  const [pEmail, setPEmail] = useState("");
  const [pPhone, setPPhone] = useState("");
  const [editContact, setEditContact] = useState(null); // { id, email, phone }
  const [confirmRedraw, setConfirmRedraw] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(() => {
    api.get(`/secret-santa/groups/${groupId}`).then(({ data }) => setData(data)).catch(() => navigate("/app/secret-santa"));
  }, [groupId, navigate]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <Loader />;
  const { group, is_owner, my_participant_id, participants } = data;
  const d = daysUntil(group.exchange_date);

  const addParticipant = async (e) => {
    e.preventDefault();
    if (!pName.trim() || !pEmail.trim() || !pPhone.trim()) { toast.error(t("ss.contact_required")); return; }
    try {
      const { data: np } = await api.post(`/secret-santa/groups/${groupId}/participants`, { name: pName.trim(), email: pEmail.trim(), phone: toStoredPhone(lang, pPhone), lang });
      setPName(""); setPEmail(""); setPPhone(""); load();
      toast.success(np.invite_sent ? t("ss.invite_auto_sent", { name: np.name }) : t("ss.invite_auto_failed"));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const saveContact = async () => {
    if (!editContact) return;
    try {
      await api.patch(`/secret-santa/groups/${groupId}/participants/${editContact.id}`, { email: editContact.email || null, phone: editContact.phone ? toStoredPhone(lang, editContact.phone) : null });
      setEditContact(null); load(); toast.success(t("ss.contact_saved"));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const waLink = (p, msg) => `https://wa.me/${p.phone || ""}?text=${encodeURIComponent(msg)}`;
  const removeParticipant = async (id) => {
    await api.delete(`/secret-santa/groups/${groupId}/participants/${id}`); load();
  };
  const doDraw = async (force) => {
    const confirmedCount = participants.filter((p) => p.confirmed).length;
    if (confirmedCount < 2) { toast.error(t("ss.min2_confirmed")); return; }
    const pending = participants.filter((p) => !p.confirmed).map((p) => p.name);
    if (pending.length && !force && !window.confirm(t("ss.pending_warning", { names: pending.join(", ") }))) return;
    try {
      const { data: res } = await api.post(`/secret-santa/groups/${groupId}/draw`, { force, lang });
      setConfirmRedraw(false); load();
      toast.success(t("ss.drawn_sent", { n: res.emails_sent ?? 0 }));
    } catch (err) {
      const d = err.response?.data?.detail;
      if (d?.code === "missing_contact") { toast.error(t("ss.missing_contact", { names: d.names.join(", ") })); return; }
      if (d?.code === "not_enough_confirmed") { toast.error(t("ss.min2_confirmed")); return; }
      if (err.response?.status === 422) toast.error(t("ss.impossible"));
      else toast.error(formatApiError(d));
    }
  };
  const copyInvite = (token) => {    navigator.clipboard?.writeText(inviteLink(token));
    toast.success(t("ss.link_copied"));
  };
  const inviteLink = (token) => `${window.location.origin}/secret-santa/confirm/${token}`;
  const shareWhatsApp = (p) => {
    const msg = t("ss.confirm_msg", { name: p.name, group: group.name }) + " " + inviteLink(p.invite_token);
    window.open(waLink(p, msg), "_blank");
  };
  const shareRevealWhatsApp = (p) => {
    const msg = t("ss.reveal_msg", { name: p.name, group: group.name }) + " " + `${window.location.origin}/secret-santa/reveal/${p.reveal_token}`;
    window.open(waLink(p, msg), "_blank");
  };
  const sendEmail = async (p) => {
    if (!p.email) { toast.error(t("ss.no_email")); return; }
    try {
      await api.post(`/secret-santa/groups/${groupId}/participants/${p.participant_id}/send-invite?lang=${lang}`);
      toast.success(t("ss.email_sent"));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const deleteGroup = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await api.delete(`/secret-santa/groups/${groupId}`);
      toast.success(t("ss.deleted"));
      navigate("/app/secret-santa");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setDeleting(false);
    }
  };

  return (
    <div data-testid="ss-group-page">
      <BackToHome crumbs={[{ label: t("nav.secretsanta"), to: "/app/secret-santa" }]} current={group.name} />
      <button onClick={() => navigate("/app/secret-santa")} className="mb-6 inline-flex items-center gap-2 text-sm text-xmas-gold hover:underline">
        <ArrowLeft className="h-4 w-4" /> {t("ss.my_groups")}
      </button>

      {/* Header */}
      <div className="glass mb-8 rounded-2xl p-6 sm:p-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="font-display text-2xl font-bold text-xmas-snow sm:text-3xl">{group.name}</h1>
          {group.drawn && <span className="rounded-full bg-xmas-gold/20 px-3 py-1 text-xs text-xmas-gold">{t("ss.drawn_badge")}</span>}
        </div>
        {group.message && <p className="mt-3 font-serif-elegant text-lg italic text-xmas-champagne">"{group.message}"</p>}
        <div className="mt-4 flex flex-wrap gap-4 text-sm text-xmas-slate">
          <span className="flex items-center gap-1"><Users className="h-4 w-4" />{participants.length} {participants.length === 1 ? t("ss.count_one") : t("ss.count")}</span>
          {group.exchange_date && <span className="flex items-center gap-1"><CalendarDays className="h-4 w-4" />{group.exchange_date}</span>}
        </div>
        {d != null && d >= 0 && (
          <p className="mt-4 font-serif-elegant text-lg italic text-xmas-gold">
            {d === 0 ? t("ss.countdown_today") : t("ss.countdown", { n: d })}
          </p>
        )}
      </div>

      {/* My reveal (if drawn and participant) */}
      {group.drawn && my_participant_id && <RevealCard groupId={groupId} t={t} />}

      {/* My wishlist (if participant) */}
      {my_participant_id && <WishlistEditor groupId={groupId} initial={data.my_wishlist} t={t} />}

      {/* Owner controls */}
      {is_owner && (
        <div className="space-y-8">
          {/* Participants */}
          <section className="glass rounded-2xl p-6" data-testid="ss-participants-section">
            <h2 className="mb-4 flex items-center gap-2 font-display text-xl text-xmas-snow"><Users className="h-5 w-5 text-xmas-gold" /> {t("ss.who_participates")}</h2>
            {!group.drawn && (
              <form onSubmit={addParticipant} className="mb-4 grid grid-cols-1 gap-2 sm:grid-cols-[1.2fr_1.4fr_1.1fr_auto]">
                <input data-testid="ss-participant-name" value={pName} onChange={(e) => setPName(e.target.value)} placeholder={t("ss.participant_name")} required className="rounded-xl border border-xmas-gold/20 bg-xmas-navy/50 px-4 py-3 text-sm text-xmas-snow outline-none focus:border-xmas-gold" />
                <input data-testid="ss-participant-email" type="email" value={pEmail} onChange={(e) => setPEmail(e.target.value)} placeholder={t("ss.email_req")} required className="rounded-xl border border-xmas-gold/20 bg-xmas-navy/50 px-4 py-3 text-sm text-xmas-snow outline-none focus:border-xmas-gold" />
                <input data-testid="ss-participant-phone" type="tel" inputMode="numeric" value={pPhone} onChange={(e) => setPPhone(maskPhone(lang, e.target.value))} placeholder={t("ss.phone_req")} required className="rounded-xl border border-xmas-gold/20 bg-xmas-navy/50 px-4 py-3 text-sm text-xmas-snow outline-none focus:border-xmas-gold" />
                <button type="submit" data-testid="ss-add-participant-btn" className="btn-gold inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-bold uppercase"><Plus className="h-4 w-4" /> {t("cta.add")}</button>
              </form>
            )}
            <div className="space-y-2">
              {participants.map((p) => (
                <div key={p.participant_id} className="flex items-center justify-between rounded-xl bg-xmas-navy/50 px-4 py-3" data-testid={`ss-participant-${p.participant_id}`}>
                  <span className="min-w-0 text-sm text-xmas-snow">
                    {p.name}
                    <span className={`ml-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wider ${p.confirmed ? "bg-green-500/15 text-green-400" : "bg-amber-500/15 text-amber-300"}`} data-testid={`ss-status-${p.participant_id}`}>
                      {p.confirmed ? <><Check className="h-3 w-3" /> {t("ss.status_confirmed")}</> : t("ss.status_pending")}
                    </span>
                    {p.confirmed && p.wish && (is_owner || group.drawn) && (
                      <span className="block text-[11px] text-xmas-champagne" data-testid={`ss-wish-${p.participant_id}`}><Gift className="mr-1 inline h-3 w-3" />{p.wish}</span>
                    )}
                    {is_owner && (
                      <span className="mt-0.5 flex flex-wrap items-center gap-2 text-[11px]">
                        <span className={p.email ? "text-xmas-slate" : "text-xmas-crimson"} data-testid={`ss-contact-email-${p.participant_id}`}>{p.email || t("ss.email_missing")}</span>
                        <span className={p.phone ? "text-xmas-slate" : "text-xmas-crimson"} data-testid={`ss-contact-phone-${p.participant_id}`}>{p.phone ? displayPhone(lang, p.phone) : t("ss.phone_missing")}</span>
                        {!group.drawn && <button type="button" onClick={() => setEditContact({ id: p.participant_id, email: p.email || "", phone: displayPhone(lang, p.phone) })} data-testid={`ss-edit-contact-${p.participant_id}`} className="text-xmas-gold hover:underline">{t("ss.edit_contact")}</button>}
                      </span>
                    )}
                  </span>
                  <div className="flex items-center gap-3">
                    {group.drawn && p.reveal_token && (
                      <button onClick={() => shareRevealWhatsApp(p)} data-testid={`ss-reveal-wa-${p.participant_id}`} title={t("ss.send_reveal_wa")}
                        className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] transition ${p.revealed ? "border-green-500/40 text-green-400" : "border-xmas-gold/30 text-xmas-champagne hover:border-xmas-gold"}`}>
                        <Gift className="h-3 w-3" /> {p.revealed ? t("ss.revealed_badge") : t("ss.send_reveal_wa")}
                      </button>
                    )}
                    {p.invite_token && (
                      <>
                        <button onClick={() => copyInvite(p.invite_token)} data-testid={`ss-invite-${p.participant_id}`} className="text-xs text-xmas-slate hover:text-xmas-gold" title={t("ss.copy_link")}>
                          <Copy className="h-4 w-4" />
                        </button>
                        <button onClick={() => shareWhatsApp(p)} data-testid={`ss-wa-${p.participant_id}`} className="text-xs text-xmas-slate hover:text-green-400" title={t("ss.whatsapp")}>
                          <MessageCircle className="h-4 w-4" />
                        </button>
                        <button onClick={() => sendEmail(p)} data-testid={`ss-email-${p.participant_id}`} className="text-xs text-xmas-slate hover:text-xmas-gold" title={t("ss.send_email")}>
                          <Mail className="h-4 w-4" />
                        </button>
                      </>
                    )}
                    {!group.drawn && (
                      <button onClick={() => removeParticipant(p.participant_id)} className="text-xmas-slate hover:text-xmas-crimson" aria-label="remove"><Trash2 className="h-4 w-4" /></button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs text-xmas-slate">{t("ss.invite_hint")}</p>
          </section>


          {/* Draw */}
          <section className="glass rounded-2xl p-6 text-center" data-testid="ss-draw-section">
            {!group.drawn ? (
              <>
                <h2 className="font-display text-xl text-xmas-snow">{t("ss.ready")}</h2>
                <p className="mx-auto mt-2 max-w-md text-sm text-xmas-slate">{t("ss.confirmed_count", { c: participants.filter((p) => p.confirmed).length, n: participants.length })}</p>
                <p className="mx-auto mt-2 max-w-lg text-xs text-xmas-slate">{t("ss.draw_note")}</p>
                <button onClick={() => doDraw(false)} data-testid="ss-draw-button" className="btn-gold mt-6 rounded-full px-8 py-4 text-sm font-bold uppercase tracking-wider">{t("ss.draw")}</button>
              </>
            ) : (
              <>
                {!confirmRedraw ? (
                  <button onClick={() => setConfirmRedraw(true)} data-testid="ss-redraw-button" className="inline-flex items-center gap-2 rounded-full border border-xmas-gold/30 px-6 py-3 text-sm text-xmas-snow hover:border-xmas-gold">
                    <ShieldAlert className="h-4 w-4" /> {t("ss.redraw")}
                  </button>
                ) : (
                  <div>
                    <p className="mb-4 text-sm text-xmas-champagne">{t("ss.redraw_confirm")}</p>
                    <div className="flex justify-center gap-3">
                      <button onClick={() => setConfirmRedraw(false)} className="rounded-full border border-xmas-gold/30 px-5 py-2.5 text-sm text-xmas-slate">{t("cta.back")}</button>
                      <button onClick={() => doDraw(true)} data-testid="ss-redraw-confirm" className="btn-gold rounded-full px-5 py-2.5 text-sm font-bold uppercase">{t("ss.redraw")}</button>
                    </div>
                  </div>
                )}
              </>
            )}
          </section>

          {/* Danger zone — delete whole group (organizer only, explicit + confirmed) */}
          <section className="rounded-2xl border border-xmas-crimson/25 bg-xmas-crimson/5 p-6" data-testid="ss-danger-section">
            {!confirmDelete ? (
              <button onClick={() => setConfirmDelete(true)} data-testid="ss-delete-group" className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm text-xmas-slate transition hover:text-xmas-crimson">
                <Trash2 className="h-4 w-4" /> {t("ss.delete_group")}
              </button>
            ) : (
              <div>
                <p className="mb-1 font-display text-lg text-xmas-snow">{t("ss.delete_group_q")}</p>
                <p className="mb-4 text-sm text-xmas-slate">{t("ss.delete_group_note")}</p>
                <div className="flex flex-wrap gap-3">
                  <button onClick={() => setConfirmDelete(false)} className="rounded-full border border-xmas-gold/30 px-5 py-2.5 text-sm text-xmas-slate hover:border-xmas-gold">{t("cta.back")}</button>
                  <button onClick={deleteGroup} disabled={deleting} data-testid="ss-delete-group-confirm" className="inline-flex items-center gap-2 rounded-full bg-xmas-crimson px-5 py-2.5 text-sm font-bold uppercase text-white transition hover:brightness-110 disabled:opacity-60">
                    <Trash2 className="h-4 w-4" /> {deleting ? "…" : t("ss.delete_group_yes")}
                  </button>
                </div>
              </div>
            )}
          </section>
        </div>
      )}
      {editContact && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="ss-edit-contact-modal" onClick={() => setEditContact(null)}>
          <div className="glass w-full max-w-sm rounded-2xl p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="mb-4 font-display text-lg text-xmas-snow">{t("ss.edit_contact")}</h3>
            <div className="space-y-3">
              <input type="email" value={editContact.email} onChange={(e) => setEditContact({ ...editContact, email: e.target.value })} placeholder={t("ss.email_req")} data-testid="ss-edit-contact-email" className="w-full rounded-xl border border-xmas-gold/20 bg-xmas-navy/50 px-4 py-3 text-sm text-xmas-snow outline-none focus:border-xmas-gold" />
              <input type="tel" inputMode="numeric" value={editContact.phone} onChange={(e) => setEditContact({ ...editContact, phone: maskPhone(lang, e.target.value) })} placeholder={PHONE_EXAMPLE[lang] || PHONE_EXAMPLE.pt} data-testid="ss-edit-contact-phone" className="w-full rounded-xl border border-xmas-gold/20 bg-xmas-navy/50 px-4 py-3 text-sm text-xmas-snow outline-none focus:border-xmas-gold" />
              <p className="text-xs text-xmas-slate">{t("ss.phone_hint", { ex: PHONE_EXAMPLE[lang] || PHONE_EXAMPLE.pt })}</p>
              <button onClick={saveContact} data-testid="ss-edit-contact-save" className="btn-gold w-full rounded-xl py-3 text-sm font-bold uppercase tracking-wider">{t("saves.save")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RevealCard({ groupId, t }) {
  const [revealed, setRevealed] = useState(false);
  const [opening, setOpening] = useState(false);
  const [receiver, setReceiver] = useState(null);
  const [suggestions, setSuggestions] = useState([]);

  const reveal = async () => {
    setOpening(true);
    try {
      const { data } = await api.get(`/secret-santa/groups/${groupId}/my-assignment?lang=${localStorage.getItem("mc_lang") || "pt"}`);
      setTimeout(() => { setReceiver(data.receiver); setSuggestions(data.suggestions || []); setRevealed(true); setOpening(false); }, 1400);
    } catch {
      setOpening(false);
    }
  };

  return (
    <div className="glass mb-8 overflow-hidden rounded-2xl p-8 text-center" data-testid="ss-reveal-card">
      {!revealed ? (
        <>
          <div className={`mx-auto mb-5 flex h-24 w-24 items-center justify-center rounded-2xl bg-xmas-red/30 ${opening ? "animate-bounce" : "pulse-glow"}`}>
            <Gift className="h-12 w-12 text-xmas-gold" />
          </div>
          <h2 className="font-display text-xl text-xmas-snow">{t("ss.waiting")}</h2>
          <button onClick={reveal} disabled={opening} data-testid="ss-reveal-button" className="btn-gold mt-6 rounded-full px-8 py-4 text-sm font-bold uppercase tracking-wider disabled:opacity-60">
            {opening ? "🎁..." : t("ss.reveal")}
          </button>
        </>
      ) : (
        <div className="float-up">
          <Sparkles className="mx-auto mb-3 h-8 w-8 text-xmas-gold" />
          <p className="text-sm uppercase tracking-widest text-xmas-slate">{t("ss.you_got")}</p>
          <h2 className="mt-2 font-display text-4xl font-black gold-gradient-text" data-testid="ss-reveal-name">{receiver.name} 🎁</h2>
          <p className="mx-auto mt-4 max-w-md text-sm text-xmas-slate">{t("ss.mission")}</p>
          {receiver.wishlist && (receiver.wishlist.items?.length || receiver.wishlist.dislikes) ? (
            <div className="mx-auto mt-6 max-w-md rounded-xl bg-xmas-navy/50 p-4 text-left">
              <p className="mb-2 text-xs uppercase tracking-widest text-xmas-gold">{t("ss.their_wishlist", { name: receiver.name })}</p>
              {receiver.wishlist.items?.map((it, i) => <p key={i} className="text-sm text-xmas-snow">• {it}</p>)}
              {receiver.wishlist.dislikes && <p className="text-sm text-xmas-slate">{t("ss.dislikes")}: {receiver.wishlist.dislikes}</p>}
            </div>
          ) : null}
          <Link to={`/app/gifts?for=${encodeURIComponent(receiver.name)}${receiver.wishlist?.items?.length ? `&q=${encodeURIComponent(receiver.wishlist.items[0])}` : ""}`} data-testid="ss-find-gift" className="btn-gold mt-6 inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold uppercase tracking-wider">
            <Gift className="h-4 w-4" /> {t("ss.find_gift_for", { name: receiver.name })}
          </Link>
          {suggestions.length > 0 && (
            <div className="mx-auto mt-6 max-w-md">
              <p className="mb-3 text-xs uppercase tracking-widest text-xmas-gold">{t("ss.suggestions", { name: receiver.name })}</p>
              <div className="grid grid-cols-2 gap-3">
                {suggestions.map((s) => (
                  <div key={s.id} className="overflow-hidden rounded-xl border border-xmas-gold/15 bg-xmas-navy/50 text-left" data-testid={`ss-suggestion-${s.id}`}>
                    <img src={s.img} alt="" loading="lazy" className="h-20 w-full object-cover" />
                    <div className="p-2">
                      <p className="truncate text-xs text-xmas-snow">{s.name}</p>
                      {s.price != null && <p className="font-mono-num text-[11px] text-xmas-champagne">~{s.price}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function WishlistEditor({ groupId, initial, t }) {
  const [items, setItems] = useState((initial?.items || []).join("\n"));
  const [dislikes, setDislikes] = useState(initial?.dislikes || "");
  const [saving, setSaving] = useState(false);
  const inputCls = "w-full rounded-xl border border-xmas-gold/20 bg-xmas-navy/50 px-4 py-3 text-sm text-xmas-snow outline-none focus:border-xmas-gold";

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/secret-santa/groups/${groupId}/wishlist`, {
        items: items.split("\n").map((s) => s.trim()).filter(Boolean),
        likes: null, dislikes: dislikes || null,
      });
      toast.success(t("ss.wishlist_saved"));
    } catch { toast.error("Error"); } finally { setSaving(false); }
  };

  return (
    <div className="glass mb-8 rounded-2xl p-6" data-testid="ss-wishlist-editor">
      <h2 className="mb-1 flex items-center gap-2 font-display text-xl text-xmas-snow"><Gift className="h-5 w-5 text-xmas-gold" /> {t("ss.wishlist")}</h2>
      <p className="mb-4 text-sm text-xmas-slate">{t("ss.wishlist_hint")}</p>
      <label className="mb-2 block text-xs uppercase tracking-widest text-xmas-slate">{t("ss.wishlist_items")}</label>
      <textarea data-testid="ss-wishlist-items" rows={4} value={items} onChange={(e) => setItems(e.target.value)} placeholder={t("ss.wishlist_add_ph")} className={inputCls} />
      <div className="mt-4">
        <label className="mb-2 block text-xs uppercase tracking-widest text-xmas-slate">{t("ss.dislikes")}</label>
        <input value={dislikes} onChange={(e) => setDislikes(e.target.value)} data-testid="ss-wishlist-dislikes" className={inputCls} />
      </div>
      <button onClick={save} disabled={saving} data-testid="ss-wishlist-save" className="btn-gold mt-5 rounded-xl px-6 py-3 text-sm font-bold uppercase tracking-wider disabled:opacity-60">
        {saving ? "..." : t("ss.save_wishlist")}
      </button>
    </div>
  );
}
